import { useEffect, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

/**
 * Hook que solicita permissão de notificação push e registra a subscription no servidor.
 * Está disponível a qualquer colaborador autenticado, inclusive responsáveis pela montagem.
 */
export function usePushNotifications() {
  const { user } = useAuth();
  const subscribeMutation = trpc.push.subscribe.useMutation();
  const { data: vapidData } = trpc.push.getVapidKey.useQuery(undefined, {
    staleTime: Infinity,
  });
  const hasSubscribed = useRef(false);

  useEffect(() => {
    if (!user) return;
    if (hasSubscribed.current) return;
    if (!vapidData?.publicKey) return;

    const vapidKey = vapidData.publicKey;

    async function subscribe() {
      try {
        // Verificar suporte
        if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;

        // Solicitar permissão
        const permission = await Notification.requestPermission();
        if (permission !== "granted") return;

        // Aguardar o service worker estar pronto
        const registration = await navigator.serviceWorker.ready;

        // Verificar se já existe uma subscription
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          // Já está subscrito — registrar no servidor se ainda não foi feito
          const keys = existing.toJSON().keys;
          if (keys?.p256dh && keys?.auth) {
            await subscribeMutation.mutateAsync({
              endpoint: existing.endpoint,
              p256dh: keys.p256dh,
              auth: keys.auth,
            });
          }
          hasSubscribed.current = true;
          return;
        }

        // Converter chave VAPID de base64url para Uint8Array
        const applicationServerKey = urlBase64ToUint8Array(vapidKey);

        // Criar nova subscription
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey,
        });

        const json = subscription.toJSON();
        if (!json.keys?.p256dh || !json.keys?.auth) return;

        await subscribeMutation.mutateAsync({
          endpoint: subscription.endpoint,
          p256dh: json.keys.p256dh,
          auth: json.keys.auth,
        });

        hasSubscribed.current = true;
      } catch (_) {
        // Silenciosamente ignorar erros (usuário pode ter bloqueado notificações)
      }
    }

    subscribe();
  }, [user, vapidData?.publicKey]);
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
